/**
 * WeaveIn.ai Platform - Program Management
 * Admin UI to manage organization programs (CalAIM ECM, CS, Housing, etc.)
 */

import React, { useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Receipt,
  Sparkles,
  X,
  UserPlus,
  ClipboardList,
} from 'lucide-react';
import usePrograms from '@/hooks/usePrograms';
import useCustomFields from '@/hooks/useCustomFields';
import useWorkflowTemplates from '@/hooks/useWorkflows';
import { ProgramCreate, ProgramCategoryCreate, programsAPI } from '@/services/api/programs';
import { useNavConfig } from '@/hooks/useNavConfig';
import { workflowsAPI } from '@/services/api/workflows';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { getActiveCboId } from '@/services/api/organizations';
import CBOProgramSetupWizard from '../cbo/CBOProgramSetupWizard';
import { HealthPlanPicker } from '../programs/HealthPlanPicker';
import { ProgramBillingActivities } from '../billing/ProgramBillingActivities';
import { ReminderConfigTab } from '../settings/ReminderConfigTab';
import { useCapability } from '@/hooks/useCapabilities';
import ProgramRAGSearch, { ProgramSearchResult } from '../search/ProgramRAGSearch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/PhoneInput';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { ProgramCaseloadModelToggle, submitProgramUpdate, useProgramCaseloadModel } from './ProgramCaseloadModelToggle';
import { ProgramCaseAssignmentSection } from './ProgramCaseAssignmentSection';
import { useIsPlatformAdmin } from '@/components/messaging/systemEvents/ChannelEventsDialog';

interface ProgramManagementProps {
  onNavigate?: (tab: string) => void;
}

export default function ProgramManagement({ onNavigate }: ProgramManagementProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;
  const { enabled: billingEnabled } = useCapability(organizationId, 'billing');
  const {
    programs,
    programTypes,
    loading,
    error,
    createProgram,
    updateProgram,
    deleteProgram,
    refetch,
  } = usePrograms();

  const { categories } = useCustomFields();
  const isPlatformAdmin = useIsPlatformAdmin();

  const { templates: workflowTemplates } = useWorkflowTemplates({ is_active: true });

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [editingProgram, setEditingProgram] = useState<any>(null);
  // TRUS-709: the caseload model is written only when the user changes it.
  const caseload = useProgramCaseloadModel(editingProgram);
  const [showBillingActivities, setShowBillingActivities] = useState(false);
  const [showReminderConfig, setShowReminderConfig] = useState(false);

  // Get CBO ID from organization context
  const cboId = getActiveCboId() || 1;
  // LCC only for now (TODO: productize via nav registry)
  const isLccEnroll = Number(getActiveCboId()) === 350;
  const [formData, setFormData] = useState<ProgramCreate>({
    name: '',
    program_type: '',
    description: '',
    is_active: true,
    start_date: '',
    end_date: '',
    capacity: undefined,
    funding_source: '',
    eligibility_criteria: {},
    services_offered: [],
    program_manager_name: '',
    program_manager_email: '',
    program_manager_phone: '',
    default_nav_tab: null,
    payer_id: null,
  });

  // The landing-tab picker is only meaningful where the program switcher exists, and
  // it offers THIS org's own pages — a hardcoded list would drift from the sidebar.
  const { enabled: programSwitcher } = useCapability(cboId, 'program_switcher');
  const { sections: navSections } = useNavConfig(cboId);
  const landingOptions = React.useMemo(
    () =>
      (navSections || [])
        .flatMap((section: any) =>
          (section.items || []).map((item: any) => ({
            value: item.nav_tab_id as string,
            label: `${section.section_name} · ${item.label}`,
          }))
        )
        .filter((o: { value: string }) => Boolean(o.value)),
    [navSections]
  );

  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  // RAG semantic search state
  const [semanticSearchResults, setSemanticSearchResults] = useState<ProgramSearchResult[]>([]);
  const [isSemanticSearchActive, setIsSemanticSearchActive] = useState(false);

  // Handle RAG search results
  const handleSemanticSearchResults = useCallback((results: ProgramSearchResult[]) => {
    setSemanticSearchResults(results);
    setIsSemanticSearchActive(results.length > 0);
  }, []);

  // Clear semantic search
  const clearSemanticSearch = () => {
    setSemanticSearchResults([]);
    setIsSemanticSearchActive(false);
  };
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedWorkflowTemplates, setSelectedWorkflowTemplates] = useState<number[]>([]);

  const resetForm = () => {
    setFormData({
      name: '',
      program_type: '',
      description: '',
      is_active: true,
      start_date: '',
      end_date: '',
      capacity: undefined,
      funding_source: '',
      eligibility_criteria: {},
      services_offered: [],
      program_manager_name: '',
      program_manager_email: '',
      program_manager_phone: '',
      default_nav_tab: null,
      payer_id: null,
    });
    setSelectedServices([]);
    setSelectedCategories([]);
    setSelectedWorkflowTemplates([]);
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingProgram(null);
    setShowBillingActivities(false);
    setShowReminderConfig(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const programData = {
      ...formData,
      services_offered: selectedServices,
    };
    const created = await createProgram(programData);
    if (created) {
      // Save category associations
      if (selectedCategories.length > 0) {
        try {
          const categoryData: ProgramCategoryCreate[] = selectedCategories.map((cat, idx) => ({
            category: cat,
            is_required: false,
            display_order: idx,
          }));
          await programsAPI.updateProgramCategoriesBulk(created.id, categoryData);
        } catch (err) {
          console.error('Error saving program categories:', err);
        }
      }
      // Save workflow template associations
      if (selectedWorkflowTemplates.length > 0) {
        try {
          for (const templateId of selectedWorkflowTemplates) {
            await workflowsAPI.updateWorkflowTemplate(templateId, {
              program_id: created.id
            });
          }
        } catch (err) {
          console.error('Error saving workflow templates:', err);
        }
      }
      resetForm();
      refetch();
    }
  };

  const handleEdit = async (program: any) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      program_type: program.program_type,
      description: program.description || '',
      is_active: program.is_active,
      start_date: program.start_date || '',
      end_date: program.end_date || '',
      capacity: program.capacity,
      funding_source: program.funding_source || '',
      billing_code: program.billing_code || '',
      reimbursement_rate: program.reimbursement_rate,
      eligibility_criteria: program.eligibility_criteria || {},
      services_offered: program.services_offered || [],
      program_manager_name: program.program_manager_name || '',
      program_manager_email: program.program_manager_email || '',
      program_manager_phone: program.program_manager_phone || '',
      max_units_period: program.max_units_period || 'month',
      default_nav_tab: program.default_nav_tab ?? null,
      payer_id: program.payer_id ?? null,
    });
    setSelectedServices(program.services_offered || []);

    // Load program categories
    try {
      const programCategories = await programsAPI.getProgramCategories(program.id);
      setSelectedCategories(programCategories.map(pc => pc.category));
    } catch (err) {
      console.error('Error loading program categories:', err);
      setSelectedCategories([]);
    }

    // Load workflow templates associated with this program
    try {
      const programWorkflows = workflowTemplates.filter(wt => wt.program_id === program.id);
      setSelectedWorkflowTemplates(programWorkflows.map(wt => wt.id));
    } catch (err) {
      console.error('Error loading program workflows:', err);
      setSelectedWorkflowTemplates([]);
    }

    setShowEditForm(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProgram) return;

    const programData = {
      ...formData,
      services_offered: selectedServices,
    };
    const updated = await submitProgramUpdate({
      programId: editingProgram.id,
      programData,
      loadedModel: caseload.loaded ?? null,
      chosenModel: caseload.chosen,
      updateProgram: (id, data) => updateProgram(id, data as Partial<ProgramCreate>),
      onCaseloadError: (err) => console.error('Error updating program caseload model:', err),
    });
    if (updated) {
      // Update category associations
      try {
        const categoryData: ProgramCategoryCreate[] = selectedCategories.map((cat, idx) => ({
          category: cat,
          is_required: false,
          display_order: idx,
        }));
        await programsAPI.updateProgramCategoriesBulk(editingProgram.id, categoryData);
      } catch (err) {
        console.error('Error updating program categories:', err);
      }
      // Update workflow template associations
      try {
        // Get currently associated workflows
        const currentWorkflows = workflowTemplates.filter(wt => wt.program_id === editingProgram.id);
        const currentIds = currentWorkflows.map(wt => wt.id);

        // Remove associations for workflows no longer selected
        for (const templateId of currentIds) {
          if (!selectedWorkflowTemplates.includes(templateId)) {
            await workflowsAPI.updateWorkflowTemplate(templateId, {
              program_id: undefined
            });
          }
        }

        // Add associations for newly selected workflows
        for (const templateId of selectedWorkflowTemplates) {
          if (!currentIds.includes(templateId)) {
            await workflowsAPI.updateWorkflowTemplate(templateId, {
              program_id: editingProgram.id
            });
          }
        }
      } catch (err) {
        console.error('Error updating workflow templates:', err);
      }
      resetForm();
      refetch();
    }
  };

  const handleDelete = async (programId: number, programName: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${programName}"?\n\nWARNING: This will also delete all member enrollments for this program!`
      )
    ) {
      await deleteProgram(programId);
    }
  };

  const toggleService = (service: string) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter((s) => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter((c) => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  const toggleWorkflowTemplate = (templateId: number) => {
    if (selectedWorkflowTemplates.includes(templateId)) {
      setSelectedWorkflowTemplates(selectedWorkflowTemplates.filter((id) => id !== templateId));
    } else {
      setSelectedWorkflowTemplates([...selectedWorkflowTemplates, templateId]);
    }
  };

  const filterPrograms = () => {
    let filtered = programs;

    // If semantic search is active, filter by matching program IDs
    if (isSemanticSearchActive && semanticSearchResults.length > 0) {
      const matchingIds = semanticSearchResults
        .map(r => r.program_id)
        .filter((id): id is number => id !== undefined);
      filtered = filtered.filter(p => matchingIds.includes(p.id));
      // Sort by semantic search score
      filtered.sort((a, b) => {
        const aResult = semanticSearchResults.find(r => r.program_id === a.id);
        const bResult = semanticSearchResults.find(r => r.program_id === b.id);
        return (bResult?.score || 0) - (aResult?.score || 0);
      });
    }

    if (filterType) {
      filtered = filtered.filter((p) => p.program_type === filterType);
    }

    if (filterActive === 'active') {
      filtered = filtered.filter((p) => p.is_active);
    } else if (filterActive === 'inactive') {
      filtered = filtered.filter((p) => !p.is_active);
    }

    return filtered;
  };

  const commonServices = [
    'care_coordination',
    'health_promotion',
    'comprehensive_assessment',
    'care_plan_development',
    'referral_assistance',
    'follow_up_care',
    'benefits_navigation',
    'community_resources',
    'housing_navigation',
    'food_assistance',
    'transportation',
    'mental_health_support',
    'substance_abuse_support',
    'chronic_disease_management',
  ];

  const getProgramTypeLabel = (typeValue: string): string => {
    const type = programTypes.find((pt) => pt.value === typeValue);
    return type ? type.label : typeValue;
  };

  return (
    <div className="container mx-auto px-4 mt-8 mb-8 max-w-7xl">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <p className="text-[13px] text-ink-500">
          Manage organization programs (CalAIM ECM, CS, Housing, Food, Transportation, etc.)
        </p>
        <div className="flex-1" />
        <div className="flex gap-2">
          {billingEnabled && (
          <Button
            variant="outline"
            icon={<Receipt className="w-4 h-4" />}
            onClick={() => onNavigate?.('program-billing')}
          >
            {t('admin.billing_activities', 'Billing Activities')}
          </Button>
          )}
          <Button
            variant="secondary"
            icon={<Sparkles className="w-4 h-4" />}
            onClick={() => setShowWizard(true)}
          >
            {t('admin.program_wizard', 'Program Wizard')}
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setShowAddForm(true)}
          >
            {t('admin.quick_add', 'Quick Add')}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>
            <strong>{t('admin.error', 'Error:')}</strong> {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Add Program Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.add_program', 'Add Program')}</DialogTitle>
            <DialogDescription>
              {t('admin.add_program_description', 'Create a new program to manage member enrollments, billing activities, and service delivery.')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_name', 'Program Name')}<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., CalAIM ECM Program"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="program_type" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_type', 'Program Type')}<span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="program_type"
                    value={formData.program_type}
                    onChange={(e) => setFormData({ ...formData, program_type: e.target.value })}
                    required
                    className="mt-1 flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                  >
                    <option value="">{t('admin.select_type', 'Select type...')}</option>
                    {programTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {formData.program_type && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {programTypes.find((pt) => pt.value === formData.program_type)?.description}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="description" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.description', 'Description')}
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('admin.program_description_and_purpose', 'Program description and purpose...')}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                  />
                  <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer">
                    Program is active
                  </Label>
                </div>

                <div>
                  <Label htmlFor="start_date" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.start_date', 'Start Date')}
                  </Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="end_date" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.end_date', 'End Date')}
                  </Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="capacity" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.capacity', 'Capacity')}
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, capacity: e.target.value ? parseInt(e.target.value) : undefined })
                    }
                    placeholder={t('admin.maximum_enrollment_capacity', 'Maximum enrollment capacity')}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="funding_source" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.funding_source', 'Funding Source')}
                  </Label>
                  <Input
                    id="funding_source"
                    type="text"
                    value={formData.funding_source}
                    onChange={(e) => setFormData({ ...formData, funding_source: e.target.value })}
                    placeholder="e.g., Medi-Cal, County Grant"
                    className="mt-1"
                  />
                </div>

                <HealthPlanPicker
                  id="payer_id"
                  value={formData.payer_id}
                  onChange={(payerId) => setFormData({ ...formData, payer_id: payerId })}
                />

                {/* Landing page. Only meaningful where the program switcher exists,
                    and it can only change where you LAND — never what you can reach. */}
                {programSwitcher && (
                  <div>
                    <Label htmlFor="default_nav_tab" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Landing page
                    </Label>
                    <select
                      id="default_nav_tab"
                      value={formData.default_nav_tab || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, default_nav_tab: e.target.value || null })
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    >
                      <option value="">Stay on the current page</option>
                      {landingOptions.map((o: { value: string; label: string }) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Where someone lands when they switch to this program. It never hides
                      a page — everything they could reach before, they can still reach.
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-500 italic mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  {t('admin.billing_configured_separately', 'Billing codes, reimbursement rates, and max units are configured in Billing Activities after program creation.')}
                </p>

                <div>
                  <Label htmlFor="program_manager_name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_name', 'Program Manager Name')}
                  </Label>
                  <Input
                    id="program_manager_name"
                    type="text"
                    value={formData.program_manager_name}
                    onChange={(e) => setFormData({ ...formData, program_manager_name: e.target.value })}
                    placeholder={t('admin.full_name', 'Full name')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="program_manager_email" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_email', 'Program Manager Email')}
                  </Label>
                  <Input
                    id="program_manager_email"
                    type="email"
                    value={formData.program_manager_email}
                    onChange={(e) => setFormData({ ...formData, program_manager_email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="program_manager_phone" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_phone', 'Program Manager Phone')}
                  </Label>
                  <PhoneInput
                    id="program_manager_phone"
                    value={formData.program_manager_phone}
                    onChange={(v) => setFormData({ ...formData, program_manager_phone: v })}
                    placeholder="(555) 555-5555"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">
                    {t('admin.services_offered', 'Services Offered')}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {commonServices.map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          id={`service-${service}`}
                          checked={selectedServices.includes(service)}
                          onCheckedChange={() => toggleService(service)}
                        />
                        <Label htmlFor={`service-${service}`} className="text-xs cursor-pointer">
                          {service.replace(/_/g, ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Field Categories Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <Label className="text-base font-semibold text-gray-900 dark:text-gray-100 block mb-2">
                {t('admin.custom_field_categories', 'Custom Field Categories')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select which custom field categories should appear when members are enrolled in this program.
                Only selected categories will be shown in the enrollment form.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {categories?.filter((cat) => cat && cat.trim() !== '').map((category) => (
                  <label
                    key={category}
                    htmlFor={`category-${category}`}
                    className={`flex items-center space-x-2 p-2 border rounded-md cursor-pointer transition-colors ${
                      selectedCategories.includes(category)
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <Checkbox
                      id={`category-${category}`}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={() => toggleCategory(category)}
                    />
                    <span className="text-sm cursor-pointer flex-1">
                      {category}
                    </span>
                  </label>
                ))}
                {(!categories || categories.filter((cat) => cat && cat.trim() !== '').length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic col-span-full">
                    {t('admin.no_custom_field_categories_defined', 'No custom field categories defined yet. Create categories in the Field Configuration Manager.')}
                  </p>
                )}
              </div>
            </div>

            {/* Workflow Templates Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <Label className="text-base font-semibold text-gray-900 dark:text-gray-100 block mb-2">
                {t('admin.workflow_templates', 'Workflow Templates')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('admin.select_workflow_templates_to_associate', 'Select workflow templates to associate with this program. These workflows will be available for program enrollments.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {workflowTemplates?.map((template) => (
                  <label
                    key={template.id}
                    htmlFor={`template-${template.id}`}
                    className={`flex items-start space-x-2 p-3 border rounded-md cursor-pointer transition-colors ${
                      selectedWorkflowTemplates.includes(template.id)
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <Checkbox
                      id={`template-${template.id}`}
                      checked={selectedWorkflowTemplates.includes(template.id)}
                      onCheckedChange={() => toggleWorkflowTemplate(template.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium cursor-pointer block">
                        {template.name}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {template.trigger_type.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </label>
                ))}
                {(!workflowTemplates || workflowTemplates.length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic col-span-full">
                    {t('admin.no_workflow_templates_available_create', 'No workflow templates available. Create templates in the Workflows section.')}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="secondary" onClick={resetForm}>
                {t('admin.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {t('admin.create_program', 'Create Program')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Program Dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.edit_program', 'Edit Program')}</DialogTitle>
            <DialogDescription>
              {t('admin.edit_program_description', 'Update program settings, custom field categories, workflow templates, and billing activities.')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_name_1', 'Program Name')}<span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="edit-name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-program_type" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_type_1', 'Program Type')}<span className="text-red-500">*</span>
                  </Label>
                  <select
                    id="edit-program_type"
                    value={formData.program_type}
                    onChange={(e) => setFormData({ ...formData, program_type: e.target.value })}
                    required
                    className="mt-1 flex h-10 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                  >
                    <option value="">{t('admin.select_type_1', 'Select type...')}</option>
                    {programTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="edit-description" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.description_1', 'Description')}
                  </Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked as boolean })}
                  />
                  <Label htmlFor="edit-is_active" className="text-sm font-medium cursor-pointer">
                    Program is active
                  </Label>
                </div>

                <div>
                  <Label htmlFor="edit-start_date" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.start_date_1', 'Start Date')}
                  </Label>
                  <Input
                    id="edit-start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-end_date" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.end_date_1', 'End Date')}
                  </Label>
                  <Input
                    id="edit-end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-capacity" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.capacity_1', 'Capacity')}
                  </Label>
                  <Input
                    id="edit-capacity"
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, capacity: e.target.value ? parseInt(e.target.value) : undefined })
                    }
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-funding_source" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.funding_source_1', 'Funding Source')}
                  </Label>
                  <Input
                    id="edit-funding_source"
                    type="text"
                    value={formData.funding_source}
                    onChange={(e) => setFormData({ ...formData, funding_source: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <HealthPlanPicker
                  id="edit-payer_id"
                  value={formData.payer_id}
                  onChange={(payerId) => setFormData({ ...formData, payer_id: payerId })}
                />

                {/* Landing page. Only meaningful where the program switcher exists,
                    and it can only change where you LAND — never what you can reach. */}
                {programSwitcher && (
                  <div>
                    <Label htmlFor="edit-default_nav_tab" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Landing page
                    </Label>
                    <select
                      id="edit-default_nav_tab"
                      value={formData.default_nav_tab || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, default_nav_tab: e.target.value || null })
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                    >
                      <option value="">Stay on the current page</option>
                      {landingOptions.map((o: { value: string; label: string }) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Where someone lands when they switch to this program. It never hides
                      a page — everything they could reach before, they can still reach.
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="edit-billing_code" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.billing_code_1', 'Billing Code')}
                  </Label>
                  <Input
                    id="edit-billing_code"
                    type="text"
                    value={formData.billing_code}
                    onChange={(e) => setFormData({ ...formData, billing_code: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-reimbursement_rate" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.reimbursement_rate_1', 'Reimbursement Rate')}
                  </Label>
                  <Input
                    id="edit-reimbursement_rate"
                    type="number"
                    step="0.01"
                    value={formData.reimbursement_rate || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        reimbursement_rate: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-max_units_period" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.max_units_period', 'Max Units Period')}
                  </Label>
                  <Select
                    value={formData.max_units_period || 'month'}
                    onValueChange={(value) => setFormData({ ...formData, max_units_period: value as any })}
                  >
                    <SelectTrigger id="edit-max_units_period" className="mt-1">
                      <SelectValue placeholder={t('admin.select_period', 'Select period')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">{t('admin.per_month', 'Per Month')}</SelectItem>
                      <SelectItem value="year">{t('admin.per_year', 'Per Year')}</SelectItem>
                      <SelectItem value="quarter">{t('admin.per_quarter', 'Per Quarter')}</SelectItem>
                      <SelectItem value="week">{t('admin.per_week', 'Per Week')}</SelectItem>
                      <SelectItem value="day">{t('admin.per_day', 'Per Day')}</SelectItem>
                      <SelectItem value="episode">{t('admin.per_episode', 'Per Episode')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.max_units_period_help', 'Time period for billing activity max units limit')}
                  </p>
                </div>

                <div>
                  <Label htmlFor="edit-program_manager_name" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_name_1', 'Program Manager Name')}
                  </Label>
                  <Input
                    id="edit-program_manager_name"
                    type="text"
                    value={formData.program_manager_name}
                    onChange={(e) => setFormData({ ...formData, program_manager_name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-program_manager_email" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_email_1', 'Program Manager Email')}
                  </Label>
                  <Input
                    id="edit-program_manager_email"
                    type="email"
                    value={formData.program_manager_email}
                    onChange={(e) => setFormData({ ...formData, program_manager_email: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-program_manager_phone" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('admin.program_manager_phone_1', 'Program Manager Phone')}
                  </Label>
                  <PhoneInput
                    id="edit-program_manager_phone"
                    value={formData.program_manager_phone}
                    onChange={(v) => setFormData({ ...formData, program_manager_phone: v })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-900 dark:text-gray-100 block mb-2">
                    {t('admin.services_offered_1', 'Services Offered')}
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    {commonServices.map((service) => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-service-${service}`}
                          checked={selectedServices.includes(service)}
                          onCheckedChange={() => toggleService(service)}
                        />
                        <Label htmlFor={`edit-service-${service}`} className="text-xs cursor-pointer">
                          {service.replace(/_/g, ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Field Categories Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <Label className="text-base font-semibold text-gray-900 dark:text-gray-100 block mb-2">
                {t('admin.custom_field_categories_1', 'Custom Field Categories')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Select which custom field categories should appear when members are enrolled in this program.
                Only selected categories will be shown in the enrollment form.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {categories?.filter((cat) => cat && cat.trim() !== '').map((category) => (
                  <label
                    key={category}
                    htmlFor={`edit-category-${category}`}
                    className={`flex items-center space-x-2 p-2 border rounded-md cursor-pointer transition-colors ${
                      selectedCategories.includes(category)
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <Checkbox
                      id={`edit-category-${category}`}
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={() => toggleCategory(category)}
                    />
                    <span className="text-sm cursor-pointer flex-1">
                      {category}
                    </span>
                  </label>
                ))}
                {(!categories || categories.filter((cat) => cat && cat.trim() !== '').length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic col-span-full">
                    {t('admin.no_custom_field_categories_defined_1', 'No custom field categories defined yet. Create categories in the Field Configuration Manager.')}
                  </p>
                )}
              </div>
            </div>

            {/* Workflow Templates Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <Label className="text-base font-semibold text-gray-900 dark:text-gray-100 block mb-2">
                {t('admin.workflow_templates_1', 'Workflow Templates')}
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('admin.select_workflow_templates_to_associate_1', 'Select workflow templates to associate with this program. These workflows will be available for program enrollments.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {workflowTemplates?.map((template) => (
                  <label
                    key={template.id}
                    htmlFor={`edit-template-${template.id}`}
                    className={`flex items-start space-x-2 p-3 border rounded-md cursor-pointer transition-colors ${
                      selectedWorkflowTemplates.includes(template.id)
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <Checkbox
                      id={`edit-template-${template.id}`}
                      checked={selectedWorkflowTemplates.includes(template.id)}
                      onCheckedChange={() => toggleWorkflowTemplate(template.id)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium cursor-pointer block">
                        {template.name}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {template.trigger_type.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </label>
                ))}
                {(!workflowTemplates || workflowTemplates.length === 0) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic col-span-full">
                    {t('admin.no_workflow_templates_available_create_1', 'No workflow templates available. Create templates in the Workflows section.')}
                  </p>
                )}
              </div>
            </div>

            {/* Billing Activities Section — only when billing capability is enabled */}
            {billingEnabled && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex justify-between items-center mb-4">
                <Label className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.billing_activities_1', 'Billing Activities')}
                </Label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowBillingActivities(!showBillingActivities)}
                >
                  {showBillingActivities ? 'Hide' : 'Configure'} Billing Activities
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('admin.configure_billable_activities_cpthcpcs_codes', 'Configure billable activities, CPT/HCPCS codes, and reimbursement rates for this program.')}
              </p>
              {showBillingActivities && editingProgram && (
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/20">
                  <ProgramBillingActivities
                    programId={editingProgram.id}
                    programName={editingProgram.name}
                    maxUnitsPeriod={editingProgram.max_units_period || formData.max_units_period || 'month'}
                    onUpdate={() => {}}
                  />
                </div>
              )}
            </div>
            )}

            {/* TRUS-709: who on the program sees its members (and their conversations). */}
            {editingProgram?.id != null && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <ProgramCaseloadModelToggle
                  programId={editingProgram.id}
                  loaded={caseload.loaded}
                  value={caseload.value}
                  onChange={caseload.setChosen}
                />
              </div>
            )}

            {/* TRUS-732: case manager field, label, closed rule and reassignment flag — super admins only. */}
            {editingProgram?.id != null && isPlatformAdmin && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <ProgramCaseAssignmentSection programId={editingProgram.id} />
              </div>
            )}

            {/* Reminder Configuration Section - ECM programs only */}
            {editingProgram && ['CalAIM_ECM'].includes(editingProgram.program_type) && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t('admin.reminder_configuration', 'Reminder Configuration')}
                  </Label>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowReminderConfig(!showReminderConfig)}
                  >
                    {showReminderConfig ? 'Hide' : 'Configure'} Reminders
                  </Button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('admin.configure_reminder_escalation', 'Configure reminder escalation thresholds, notification channels, and reassessment windows for this program.')}
                </p>
                {showReminderConfig && (
                  <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/20">
                    <ReminderConfigTab
                      programId={editingProgram.id}
                      programName={editingProgram.name}
                    />
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="secondary" onClick={resetForm}>
                {t('admin.cancel_1', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {t('admin.update_program', 'Update Program')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filter Bar with Semantic Search */}
      <Card className="mb-6">
        <CardContent className="p-4 space-y-4">
          {/* Semantic Search Row */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <ProgramRAGSearch
                onResults={handleSemanticSearchResults}
                onProgramSelect={(programId) => {
                  const program = programs.find(p => p.id === programId);
                  if (program) {
                    handleEdit(program);
                  }
                }}
                placeholder={t('admin.search_programs_semantically', 'Search programs by description, services, eligibility...')}
                showSuggestions={true}
                showSimilarPrograms={true}
              />
            </div>
            {isSemanticSearchActive && (
              <Button
                variant="outline"
                size="sm"
                icon={<X className="w-4 h-4" />}
                onClick={clearSemanticSearch}
              >
                Clear Search
              </Button>
            )}
          </div>

          {/* Semantic Search Active Indicator */}
          {isSemanticSearchActive && (
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
              <Sparkles className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <span className="text-sm text-indigo-700 dark:text-indigo-300">
                Showing {semanticSearchResults.length} semantically matched program{semanticSearchResults.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Traditional Filters Row */}
          <div className="flex flex-wrap gap-4 items-center pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Label htmlFor="filter-type" className="text-sm font-medium whitespace-nowrap">
                {t('admin.filter_by_type', 'Filter by type:')}
              </Label>
              <select
                id="filter-type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2"
              >
                <option value="">{t('admin.all_types', 'All Types')}</option>
                {programTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Label htmlFor="filter-status" className="text-sm font-medium whitespace-nowrap">
                {t('admin.status', 'Status:')}
              </Label>
              <select
                id="filter-status"
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                className="h-10 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:focus-visible:ring-teal-500 focus-visible:ring-offset-2"
              >
                <option value="all">{t('admin.all_programs', 'All Programs')}</option>
                <option value="active">{t('admin.active_only', 'Active Only')}</option>
                <option value="inactive">{t('admin.inactive_only', 'Inactive Only')}</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Programs Table */}
      {loading && programs.length === 0 ? (
        <Card>
          <CardContent className="p-12 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">{t('admin.loading_programs', 'Loading programs...')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.program_name_2', 'Program Name')}</TableHead>
                  <TableHead>{t('admin.type', 'Type')}</TableHead>
                  <TableHead>{t('admin.status_1', 'Status')}</TableHead>
                  <TableHead>{t('admin.enrollment', 'Enrollment')}</TableHead>
                  <TableHead>{t('admin.manager', 'Manager')}</TableHead>
                  <TableHead>{t('admin.funding', 'Funding')}</TableHead>
                  <TableHead>{t('admin.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filterPrograms().map((program) => (
                  <TableRow key={program.id} className={program.is_active ? '' : 'opacity-60'}>
                    <TableCell>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{program.name}</div>
                        {program.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{program.description}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Badge variant="default">{getProgramTypeLabel(program.program_type)}</Badge>
                        {program.services_offered && program.services_offered.length > 0 && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {program.services_offered.length} services
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {program.is_active ? (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            {t('admin.active', 'Active')}
                          </span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400 font-medium">
                            {t('admin.inactive', 'Inactive')}
                          </span>
                        )}
                        {program.start_date && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Started: {fmt.short(program.start_date)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {program.current_enrollment}
                          {program.capacity && ` / ${program.capacity}`}
                        </div>
                        {program.capacity && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {program.capacity - program.current_enrollment} available
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-gray-900 dark:text-gray-100">
                          {program.program_manager_name || '-'}
                        </div>
                        {program.program_manager_email && (
                          <a
                            href={`mailto:${program.program_manager_email}`}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-1 block"
                          >
                            {program.program_manager_email}
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="text-gray-900 dark:text-gray-100">
                          {program.funding_source || '-'}
                        </div>
                        {program.payer_name && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            {program.payer_name}
                          </div>
                        )}
                        {program.reimbursement_rate && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            ${program.reimbursement_rate}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          icon={<UserPlus className="w-3.5 h-3.5" />}
                          onClick={() => {
                            localStorage.setItem('pendingEnrollmentProgramId', String(program.id));
                            onNavigate?.('enrollment-new');
                          }}
                        >
                          {t('admin.enroll_member', 'Enroll')}
                        </Button>
                        {/* View enrollments drill-in — LCC only for now (TODO: productize via nav registry) */}
                        {isLccEnroll && (
                          <Button
                            size="sm"
                            variant="secondary"
                            icon={<ClipboardList className="w-3.5 h-3.5" />}
                            onClick={() => {
                              // Clear any stale single-enrollment selection so the
                              // list (pre-filtered to this program) renders, not a detail.
                              localStorage.removeItem('selectedEnrollmentId');
                              localStorage.setItem('enrollments_program_filter', String(program.id));
                              onNavigate?.('enrollments-view');
                            }}
                          >
                            {t('admin.view_enrollments', 'Enrollments')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<Pencil className="w-3.5 h-3.5" />}
                          onClick={() => handleEdit(program)}
                        >
                          {t('admin.edit', 'Edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          icon={<Trash2 className="w-3.5 h-3.5" />}
                          onClick={() => handleDelete(program.id, program.name)}
                        >
                          {t('admin.delete', 'Delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filterPrograms().length === 0 && (
            <div className="p-12 text-center">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('admin.no_programs_found', 'No programs found')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {programs.length === 0
                  ? 'Create your first program to start managing member enrollments.'
                  : 'No programs match the current filters.'}
              </p>
              <Button variant="primary" onClick={() => setShowAddForm(true)}>
                {t('admin.add_program_1', 'Add Program')}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Program Stats */}
      {programs.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('admin.program_statistics', 'Program Statistics')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {programs.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.total_programs', 'Total Programs')}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">
                  {programs.filter((p) => p.is_active).length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.active_programs', 'Active Programs')}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {programs.reduce((sum, p) => sum + p.current_enrollment, 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.total_enrollments', 'Total Enrollments')}
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                  {programs.filter((p) => p.capacity).reduce((sum, p) => sum + (p.capacity || 0), 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.total_capacity', 'Total Capacity')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CBO Program Setup Wizard */}
      {showWizard && (
        <div
          className="fixed inset-0 z-[10001] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowWizard(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] overflow-auto z-[10002]"
            onClick={(e) => e.stopPropagation()}
          >
            <CBOProgramSetupWizard
              cboId={cboId}
              onComplete={(programId) => {
                setShowWizard(false);
                refetch();
              }}
              onCancel={() => setShowWizard(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
